<script>
    import $util from '../../global/util';

    const useIntersectionObserver =
        'IntersectionObserver' in window &&
        'IntersectionObserverEntry' in window &&
        'isIntersecting' in IntersectionObserverEntry.prototype;


    /**
     * Fallback method for browsers that do not support IntersectionObserver
     */
    function scroll() {
        const vr = this === window
            ? { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth }
            : this.getBoundingClientRect();
        const vi = this.__vi;
        for (let i = 0; i < vi.length; i++) {
            const er = vi[i].$el.getBoundingClientRect();
            const visible =
                er.left < vr.right &&
                er.right > vr.left &&
                er.top < vr.bottom &&
                er.bottom > vr.top;
            if (vi[i].visible !== visible) {
                vi[i].visible = visible;
                vi[i].$emit(visible ? 'show' : 'hide', vi[i].data);
            }
        }
    }


    /**
     * A component that can act as a wrapper around content and emit events when that content
     * becomes visible, or becomes invisible. By specifying a margin, you can lazy-load content
     * that is likely to soon become visible. You can also use this without slot content, simply
     * as a waypoint; in this case a single `<i>` element will be generated. This component
     * watches visibility with respect to some root element that can scroll (by default, the
     * nearest parent that can be user scrolled if it overflows), and it monitors visibility
     * changes caused by the `display` property on any ancestor.
     *
     * This component is efficient when monitoring many children of the same root node. Because
     * of that, the recommended implementation for lazy loading many entries in a list is to wrap
     * every entry in `ib-visibility`, and debounce the `show` events to throttle server requests.
     *
     * `ib-visiblity` must have only one direct child in its slot. This is a limitation of Vue
     * abstract components.
     *
     * This component emits a `show` or `hide` event immediately upon mount.
     */
    export default {
        name: 'ib-visibility',

        props: {
            /**
             * Selector for Scrolling element with which we are monitoring visibility with respect to, or `"window"`
             * for the `window`. By default, the nearest parent with `overflow:scroll;` or `overflow:auto;` will be
             * monitored.
             */
            root: String,
            /**
             * CSS margin to extend or reduce the visibility trigger area. This is only taken into account on browsers
             * that support the `IntersectionObserver` API.
             */
            rootMargin: String,
            /**
             * If `true`, stop monitoring and do not emit `show` or `hide` events.
             */
            inert: Boolean,
            /**
             * Data to pass as argument to `show` and `hide` events.
             */
            data: null
        },

        watch: {
            root(root) {
                if (this.inert) {
                    this.disconnect();
                    this.connect(root);
                }
            },

            inert(inert) {
                if (inert) {
                    this.disconnect();
                } else {
                    this.connect(this.root);
                }
            }
        },

        created() {
            const slot = this.$slots.default || this.$scopedSlots.default;
            if (slot && slot.length > 1) {
                throw 'Multiple direct children not supported';
            }
        },

        mounted() {
            this.$el._vis = this;
            if (!this.inert) {
                this.connect(this.root);
            }
        },

        beforeDestroy() {
            this.disconnect();
            delete this.$el._vis;
        },

        render(h) {
            const slot = this.$slots.default || this.$scopedSlots.default;
            return slot ? slot[0] : h('i');
        },

        methods: {
            /**
             * Instantiate an IntersectionObserver on the root if necessary, and observe this.$el.
             */
            connect(root) {
                root = root ? root == 'window' ? window : this.$el.closest(root) : ($util.scrollParent(this.$el) || window);
                if (!root) {
                    throw new Error(root + ' not found');
                }
                this._root = root;
                this.visible = null;
                if (useIntersectionObserver) {
                    if (root.__vi === void 0) {
                        root.__vi = {
                            observer: new IntersectionObserver(entries => {
                                for (let i = 0; i < entries.length; i++) {
                                    const self = entries[i].target._vis;
                                    if (self) {
                                        self.$emit(entries[i].isIntersecting ? 'show' : 'hide', self.data);
                                    }
                                }
                            }, {
                                root: root === window ? null : root,
                                rootMargin: this.rootMargin
                            }),
                            cnt: 1
                        };
                    } else {
                        root.__vi.cnt++;
                    }
                    root.__vi.observer.observe(this.$el);
                } else {
                    if (root.__vi === void 0) {
                        root.__vi = [this];
                        root.__vn = scroll;
                        root.addEventListener('scroll', scroll);
                        addEventListener('resize', root.__vn);
                    } else {
                        root.__vi.push(this);
                    }
                    if (!root.__vi.dirty) {
                        root.__vi.dirty = true;
                        this.$nextTick(() => {
                            root.__vi.dirty = false;
                            root.__vn();
                        })
                    }
                }
            },

            /**
             * Unobserve this.$el, and disconnect IntersectionObserver if nothing to observe.
             */
            disconnect() {
                const root = this._root;
                if (root && root.__vi) {
                    if (useIntersectionObserver) {
                        root.__vi.observer.unobserve(this.$el);
                        if (!(--root.__vi.cnt)) {
                            root.__vi.observer.disconnect();
                            delete root.__vi;
                        }
                    } else {
                        root.__vi = root.__vi.filter(e => e !== this);
                        if (!root.__vi.length) {
                            root.removeEventListener('scroll', scroll);
                            removeEventListener('resize', root.__vn);
                            delete root.__vi;
                            delete root.__vn;
                        }
                    }
                }
            }
        }
    }
</script>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        ib-visibility(root="#main", @show="$notify('Show', { unique: true })", @hide="$notify('Hide', { unique: true })")
            div Scroll me into or out of view.
    </template>
</ib-example>
